import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useRef } from 'react';
import React from 'react';
import useCommonStore, { ModalConfig } from '@/stores/common';
import ConfirmModal from './ConfirmModal';
import ScheduleLessonModal from './ScheduleLessonModal';
import UpdateTransportModal from './UpdateTransportModal';
import LessonEntryHelpModal from './LessonEntryHelpModal';
import ProcessedLessonDetailsModal from './ProcessedLessonDetailsModal';
import AddParticipantModal from './AddParticipantModal';
import UpdateParticipantModal from './UpdateParticipantModal';

const CommonModal = () => {
  const { modalConfig = {} as ModalConfig, hideCommonModal } = useCommonStore<{
    modalConfig?: ModalConfig;
    hideCommonModal: () => void;
  }>(state => state);

  const initialFocusRef = useRef<HTMLButtonElement>(null);

  const modalBodyContent: Record<string, React.JSX.Element> = {
    confirmModal: <ConfirmModal />,
    scheduleLessonModal: <ScheduleLessonModal />,
    updateTransportModal: <UpdateTransportModal />,
    lessonEntryHelpModal: <LessonEntryHelpModal />,
    processedLessonDetailsModal: <ProcessedLessonDetailsModal />,
    addParticipantModal: <AddParticipantModal />,
    updateParticipantModal: <UpdateParticipantModal />
  };

  const handleClose = () => {
    if (modalConfig?.onClose) {
      modalConfig.onClose();
      return;
    }
    hideCommonModal();
  };

  const modalSize = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    '2xl': 'max-w-7xl',
    rem90: 'max-w-[90rem]',
    '3xl': 'max-w-9xl',
    '4xl': 'max-w-11xl',
    '5xl': 'max-w-13xl',
    '6xl': 'max-w-15xl',
    '7xl': 'max-w-17xl',
    '8xl': 'max-w-19xl',
    '9xl': 'max-w-21xl'
  };

  return (
    <Transition appear show={modalConfig?.visible} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-50'
        onClose={handleClose}
        initialFocus={initialFocusRef}
        {...modalConfig?.props}
      >
        <Transition.Child as={Fragment}>
          <div className='fixed inset-0 bg-black bg-opacity-50' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex w-full min-h-full items-center justify-center p-4'>
            <div className='relative w-full'>
              <Transition.Child as={Fragment}>
                <Dialog.Panel
                  className={`relative w-full mx-auto rounded-lg bg-white shadow-xl z-1 ${
                    modalSize[
                      (modalConfig?.props?.size as keyof typeof modalSize) ??
                        'md'
                    ]
                  }`}
                >
                  <div className='flex justify-end p-2'>
                    <button
                      type='button'
                      ref={initialFocusRef}
                      onClick={handleClose}
                      className='rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500'
                      aria-label='Close modal'
                    >
                      <svg
                        className='h-6 w-6'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        aria-hidden='true'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                  </div>
                  {modalConfig?.type && modalBodyContent[modalConfig.type] && (
                    <div className='w-full'>
                      {modalBodyContent[modalConfig.type]}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CommonModal;
